import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { languageHelper } from "../../config/helperFunctions";
import axios from "axios";
import { apiKey } from "../../config/config";
import { toast } from "react-toastify";

function OTPPage({ logo }) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get("email");
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleChange = (element, index) => {
    if (isNaN(element.value)) return false;

    setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);

    // Focus next input
    if (element.value !== "") {
      if (index < 5 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0 && inputRefs.current[index - 1]) {
      // Move focus to previous input on backspace if current is empty
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6).split("");
    if (pastedData.some(isNaN)) return;

    const newOtp = [...otp];
    pastedData.forEach((value, index) => {
      newOtp[index] = value;
    });
    setOtp(newOtp);

    // Focus the last filled input or the first empty one
    const focusIndex = pastedData.length < 6 ? pastedData.length : 5;
    if (inputRefs.current[focusIndex]) {
      inputRefs.current[focusIndex].focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length === 6) {
      axios.post(`${apiKey}/api/rms/customer/verify-otp`, { email, otp: otpValue })
        .then((res) => {
          toast.success(languageHelper("OTP Verified Successfully"), {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
          localStorage.setItem("rms-customer", JSON.stringify(res.data.data));
          navigate("/");
        })
        .catch((err) => {
          toast.error(err.response.data.message || languageHelper("Something went wrong"), {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
        });
    }
  };

  const handleResendOTP = (e) => {
    e.preventDefault();
    if (cooldown > 0) return;

    setCooldown(60);

    axios.post(`${apiKey}/api/rms/customer/resend-otp`, { email })
      .then((res) => {
        toast.success(languageHelper("OTP Resent Successfully"), {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      })
      .catch((err) => {
        toast.error(err.response.data.message || languageHelper("Something went wrong"), {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      });
  };

  return (
    <div className="grid place-items-center max-w-7xl mx-auto w-[90%] min-h-[80vh] px-4 py-16">
      <div className="max-w-2xl w-full">
        <div className="text-w-heading">
          {logo && <img className="mx-auto w-56" src={logo} alt="" />}
          <h1 className="text-xl sm:text-[2rem] font-medium text-center mt-4">
            {languageHelper("Enter OTP")}
          </h1>
          <p className="text-center text-sm mt-2 opacity-80">
            {languageHelper("We have sent a 6-digit verification code to your email.")}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="w-full max-w-sm mx-auto mt-10">
          <div
            className="flex justify-center gap-2 sm:gap-3 w-full"
            onPaste={handlePaste}
          >
            {otp.map((data, index) => (
              <input
                key={index}
                type="text"
                name="otp"
                maxLength="1"
                ref={(el) => (inputRefs.current[index] = el)}
                value={data}
                onChange={(e) => handleChange(e.target, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onFocus={(e) => e.target.select()}
                className="w-10 h-12 sm:w-14 sm:h-14 border rounded text-center text-xl sm:text-2xl font-semibold text-w-heading focus:border-w-bg focus:ring-1 focus:ring-w-bg outline-none transition-all bg-white shadow-sm"
              />
            ))}
          </div>
          <div className="w-full mt-10 flex">
            <button
              type="submit"
              disabled={otp.join("").length !== 6}
              className="bg-w-btn text-w-btn-text max-w-[12rem] w-full px-2 py-2 rounded mx-auto text-center font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
            >
              {languageHelper("Submit")}
            </button>
          </div>
        </form>
        <div className="mt-20 w-full">
          <p className="text-center text-sm font-medium">
            {languageHelper("Didn't receive the code?")}
            <button
              onClick={handleResendOTP}
              disabled={cooldown > 0}
              className={`ml-2 transition-colors ${cooldown > 0 ? "text-gray-400 cursor-not-allowed" : "text-w-bg hover:underline"}`}
            >
              {cooldown > 0 ? `${languageHelper("Resend OTP")} (${cooldown}s)` : languageHelper("Resend OTP")}
            </button>
          </p>
          <div className="text-center mt-4">
            <Link to="/signup" className="text-sm text-gray-500 hover:text-w-bg hover:underline">
              {languageHelper("Back to Signup")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OTPPage;